
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem, ColumnType } from "../../components/Table/TableRow";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, routerStore, languageStore } from "../_rootStore";
import ImageService from "../../services/ImageService";

// MARK: Resources
import strings from "../../resources/strings";

export default class BedroomStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();

	// Control
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public bedrooms: api.Bedroom[] = [];
	@observable public selectedBedroom: api.Bedroom | null = null;

	// Variables
	@observable public name: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public url: string = "";
	@observable public images: api.UncertainImage[] = [];
	@observable public modal: boolean = false;
	@observable public bedroomComfortDescription: string = "";
	@observable public order: string = "0";
	@observable public bedroomComfortType: api.BedroomConfort = api.BedroomConfort.size;
	@observable public bedroomSpecification:  api.BedroomSpecification[] = [];
	@observable public bedroomComforts: api.BedroomConfort[] = [];
	@observable public loadingBedroomComfort: boolean = false;

	@computed
	public get header() {
		const header = strings.bedrooms.table.header;
		return [
			header.picture,
			header.nick,
			header.name,
			header.description,
		];
	}

	private static formatBedroom(bedroom: api.Bedroom): IRowItem {
		const bedroomInSelectedLanguage = bedroom.languages.filter((bedroomLanguage) =>
		bedroomLanguage.language === languageStore.selectedLanguage)[0].translation;

		return {
			id: bedroom.id,
			name: bedroomInSelectedLanguage.name,
			columns: [
				{
					value: bedroomInSelectedLanguage.images[0].url,
					type: ColumnType.avatar,
				},
				{
					value: bedroomInSelectedLanguage.nick,
				},
				{
					value: bedroomInSelectedLanguage.name,
				},
				{
					value: bedroomInSelectedLanguage.description,
				},
			],
		};
	}

	@computed
	public get bedroomsTableRows(): IRowItem[] {
		return this.bedrooms.map(BedroomStore.formatBedroom);
	}

	@action
	public getBedroom = async (bedroomId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.selectedBedroom = await api.getBedroom(bedroomId);
			this.setEditorFields(this.selectedBedroom);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getBedroomComfort = () => {
		if (this.loadingBedroomComfort) {
			return;
		}
		this.loadingBedroomComfort = true;

		try {
			this.bedroomComforts = api.allValuesBedroomConfort();
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loadingBedroomComfort = false;
		}
	};

	@action
	public getBedrooms = async (pageOffSet?: number) => {
		if (this.loading) {
			return;
		}

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		this.loading = true;

		try {
			this.bedrooms = await api.getBedrooms(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public createOrEditBedroom = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			if (!this.selectedBedroom) {
				const newBedroom: api.NewBedroom = {
					name: this.name,
					nick: this.nick,
					description: this.description !== "" ? this.description : "",
					url: this.url !== "" ?  this.url : "",
					images: this.imageService.getUncertainfiedImages(),
					bedroomSpecifications: this.bedroomSpecification.length > 0 ? this.bedroomSpecification : [],
					order: +this.order,
				};
				this.selectedBedroom = await api.createBedroom(newBedroom);
			} else {
				const editedBedroom: api.EditBedroom = {
					language: languageStore.selectedLanguage,
					nick: this.nick,
					name: this.name,
					description: this.description !== "" ? this.description : "",
					url: this.url !== "" ?  this.url : "",
					images: this.imageService.getUncertainfiedImages(),
					bedroomSpecifications: this.bedroomSpecification.length > 0 ? this.bedroomSpecification : [],
					order: +this.order,
				};
				this.selectedBedroom = await api.editBedroom(this.selectedBedroom.id, editedBedroom);
			}
			routerStore.replace("/dashboard/bedrooms");
			window.location.reload();
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public deleteBedroom = async (bedroomId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			await api.deleteBedroom(bedroomId);
			this.bedrooms = this.bedrooms.filter((bedroom) =>  bedroom.id !== bedroomId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public openDeleteBedroomDialog = async (bedroomId: string, bedroomName: string) => {
		uiStore.showDialog({
			title: strings.bedrooms.deleteBedroomDialog.title,
			message: `${strings.bedrooms.deleteBedroomDialog.message}: ${bedroomName}`,
		}, () => this.deleteBedroom(bedroomId));
	};

	@action
	public nextPage = async () => {
		await this.getBedrooms(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getBedrooms(this.pageOffSet - 1);
		}
	};

	@action
	public clear = () => {
		this.imageService.clear();
		this.name = "";
		this.nick = "";
		this.description = "";
		this.url = "";
		this.bedroomSpecification = [];
		this.order = "";
	};

	@action
	public setEditorFields = (bedroom: api.Bedroom) => {
		const bedroomInSelectedLanguage = bedroom.languages.filter((bedroomLanguage) =>
		bedroomLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setImages(bedroomInSelectedLanguage.images);
		this.name = bedroomInSelectedLanguage.name;
		this.nick = bedroomInSelectedLanguage.nick;
		this.description = bedroomInSelectedLanguage.description ? bedroomInSelectedLanguage.description : "";
		this.url = bedroomInSelectedLanguage.url ? bedroomInSelectedLanguage.url : "";
		this.bedroomSpecification = bedroom.bedroomSpecifications ? bedroom.bedroomSpecifications : [];
		this.order = bedroom.order ? bedroom.order.toString() : "";
	};

	@action
	public startBedroomCreateOrEdit(bedroomId?: string) {
		if (bedroomId) {
			this.getBedroom(bedroomId);
			routerStore.push(`/dashboard/bedrooms/editor/${bedroomId}`);
		} else {
			this.selectedBedroom = null;
			this.clear();
			routerStore.push("/dashboard/bedrooms/editor/");
		}
	}

	@action
	public openModal = () => {
		this.modal = true;
	};

	@action
	public closeModal = () => {
		this.modal = false;
	};

	@action
	public selectSpecificationType = (type: api.BedroomConfort) => {
		this.bedroomComfortType = type;
	};

	@action
	public saveSpecificationDetails = () => {
		if (this.bedroomComfortDescription !== "") {
			if (this.bedroomSpecification.length < 10) {
				const newSpecificationDetail: api.BedroomSpecification = {
					details: this.bedroomComfortDescription,
					type: this.bedroomComfortType,
				};
				this.bedroomSpecification = [...this.bedroomSpecification, newSpecificationDetail];
				this.bedroomComfortDescription = "";
				this.closeModal();
			} else {
				uiStore.showSnackbar(strings.bedrooms.createOrEdit.sections.specifications.errors.fullBedroomSpecifications);
			}
		} else {
			uiStore.showSnackbar(strings.bedrooms.createOrEdit.sections.specifications.errors.emptyDescription);
		}
	};

	@action
	public removeSpecificationDetails = (specification: api.BedroomSpecification) => {
		const findSpecification = this.bedroomSpecification.
			find((specificationFinder) => (specificationFinder.details === specification.details) && (specificationFinder.type === specification.type));
		if (findSpecification) {
			const index = this.bedroomSpecification.indexOf(findSpecification);
			if (index > -1) {
				this.bedroomSpecification.splice(index, 1);
			}
		}
	};
}
